import { Component, EventEmitter, forwardRef, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SessionService } from '@app/core/services/session/session.service';
import { EntitySelectionModel } from '@components/data/data-table-new/entitySelectionModel';
import { combineLatest, Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { GetHierarchyDto, NodeDto } from '../../models/hierarchy.model';
import { HierarchyService } from '../../services/hierarchy.service';
import { HierarchySelectComponent } from '../hierarchy-select/hierarchy-select.component';
import { HierarchyStringTreeComponent } from '../hierarchy-string-tree/hierarchy-string-tree.component';

export interface HierarchyNodeSelection {
  hierarchy: GetHierarchyDto;
  node: NodeDto;
}

@Component({
  selector: 'vfs-hierarchy-node-selection',
  templateUrl: './hierarchy-node-selection.component.html',
  styleUrls: ['./hierarchy-node-selection.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => HierarchyNodeSelectionComponent)
    }
  ]
})
export class HierarchyNodeSelectionComponent implements OnInit, ControlValueAccessor {
  @Input() hierarchyType: string;
  hierarchy: GetHierarchyDto;
  hierarchy$: Observable<GetHierarchyDto>;
  node$: Observable<NodeDto>;
  node: NodeDto;
  all$: Observable<any>;
  selection: EntitySelectionModel<NodeDto> = new EntitySelectionModel();
  stringTreeData: string | string[] = [];
  disabledStringTree: boolean;
  @Input() nodeTypeTag: string;
  @Input() ngModel: string[];
  @Input() required: boolean;
  @Input() disabled: boolean;
  @Output() ngModelChange = new EventEmitter<string[]>();
  @Output() selectedChange = new EventEmitter<HierarchyNodeSelection>();
  @Output() clear = new EventEmitter();
  @ViewChild(HierarchySelectComponent) hierarchySelect: HierarchySelectComponent;
  @ViewChild(HierarchyStringTreeComponent)
  hierarchyStringTree: HierarchyStringTreeComponent;
  // ControlValueAccessor
  value: any;

  constructor(protected hierarchyService: HierarchyService, protected sessionService: SessionService) {}

  ngOnInit() {
    if (this.ngModel && this.ngModel[1]) {
      this.hierarchy$ = this.hierarchyService.getHierarchy(this.ngModel[1]).pipe(tap(hie => (this.hierarchy = hie)));
      this.node$ = this.hierarchyService.getHierarchyNode(this.ngModel[1], this.ngModel[2]);
      this.all$ = combineLatest(this.hierarchy$, this.node$).pipe(
        map(result => ({ hierarchy: result[0], node: result[1] })),
        tap(result => {
          this.disabledStringTree = !this.checkHierarchyDivisions(result.hierarchy);
          this.selectedChange.emit(result);
        })
      );
    } else if (!this.ngModel || this.ngModel.length === 0) {
      this.all$ = of({ hierarchy: {} as GetHierarchyDto, node: [] });
      this.ngModel = [];
    }
  }

  // nodeStrategy: StringTableStrategy<NodeDto> = function (selection: EntitySelectionModel<NodeDto>) {
  //   return selection.selected.map(this.hierarchyService.getNodeDescriptor);
  // };

  selectedNode() {
    this.ngModelChange.emit(this.getHierarchyNodeSelectionResponse(this.selection.selected[0]));
    this.selectedChange.emit({ hierarchy: this.hierarchy, node: this.node });
  }

  private getHierarchyNodeSelectionResponse(node: NodeDto): string[] {
    return node ? [this.nodeTypeTag, node.hierarchyId, node.id] : null;
  }

  public hierarchyChange(hierarchy: GetHierarchyDto) {
    this.hierarchy = hierarchy;
    this.stringTreeData = [];
    this.selection.clear();
    this.disabledStringTree = !this.checkHierarchyDivisions(hierarchy);
    this.ngModelChange.emit(this.getHierarchyNodeSelectionResponse({} as NodeDto));
    this.selectedChange.emit({ hierarchy: this.hierarchy, node: {} as NodeDto });
  }

  public nodeChange(node: NodeDto[]) {
    this.node = node[0];
  }

  checkHierarchyDivisions(hierarchy: GetHierarchyDto): boolean {
    const divisions: string[] = this.sessionService
      .getUser()
      .userDivisions.map(ud => (ud.division.active ? ud.division.id : null))
      .filter(d => d !== null);
    const hierarchyDivisions: string[] = hierarchy.divisions
      .map(ud => (ud.division.active ? ud.division.id : null))
      .filter(d => d !== null);
    return divisions.some(div => hierarchyDivisions.includes(div));
  }

  isValid(): boolean {
    return (
      this.hierarchySelect &&
      this.hierarchySelect.ngModelIsValid() &&
      this.hierarchyStringTree &&
      this.hierarchyStringTree.isValid() &&
      this.ngModel.length === 3 &&
      this.ngModel.every(i => i != null)
    );
  }

  private setValues(nodeId: string) {}

  // ControlValueAccessor functions
  onChange = (rating: number) => {};
  onTouched = () => {};

  writeValue(obj: any): void {
    this.value = obj;
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
